import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import fastdom from 'fastdom';
import { debounce } from 'lodash';

export const MOBILE_MAX = 767;
export const TABLET_MIN = 768;
export const TABLET_MAX = 1024;
export const DESKTOP_MIN = 1025;

interface ViewSizeContextType {
  viewWidth: number;
  viewHeight: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  isReady: boolean; // True after first measurement on client
}

const ViewSizeContext = createContext<ViewSizeContextType | undefined>(undefined);

interface ViewSizeProviderProps {
  children: ReactNode;
}

export const ViewSizeProvider: React.FC<ViewSizeProviderProps> = ({ children }) => {
  // Do not use default/fallback values - wait for real measurements -Gabe
  // Server-side: Will use undefined/0 values until client hydrates
  // Client-side: Will measure actual window dimensions immediately
  const [viewWidth, setViewWidth] = useState<number>(0);
  const [viewHeight, setViewHeight] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false); // Starts false, true after first measurement

  const measureViewSize = () => {
    fastdom.measure(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setViewWidth(w);
      setViewHeight(h);
      setIsMobile(w <= MOBILE_MAX);
      setIsTablet(w >= TABLET_MIN && w <= TABLET_MAX);
      setIsDesktop(w >= DESKTOP_MIN);
      setIsReady(true); // Mark as ready after first measurement
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const debouncedMeasure = debounce(measureViewSize, 50);

    // Measure once
    measureViewSize();

    // Measure on resize
    window.addEventListener('resize', debouncedMeasure);
    return () => {
      window.removeEventListener('resize', debouncedMeasure);
      debouncedMeasure.cancel();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkTouchDevice = () => {
      setIsTouchDevice(('ontouchstart' in window) || (navigator?.maxTouchPoints > 0));
    };
    checkTouchDevice();
  }, []);

  const value: ViewSizeContextType = {
    viewWidth,
    viewHeight,
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    isReady,
  };

  return (
    <ViewSizeContext.Provider value={value}>
      {children}
    </ViewSizeContext.Provider>
  );
};

export const useViewSize = (): ViewSizeContextType => {
  const context = useContext(ViewSizeContext);
  return context || {
    viewWidth: 0,
    viewHeight: 0,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isTouchDevice: false,
    isReady: false,
  };
};
